import { cn } from '@repo/lib/cn';
import { getFileExtension } from '@repo/lib/fs/getFileExtension';
import { useState } from 'react';

import MediaViewer from '@/components/features/media/MediaViewer';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { FileIcon } from '@/components/ui/file-icon';
import { FileCard, FileCardContent, FileCardPreview, FileCardPreviewFallback, FileCardPreviewImage, FileCardTitle } from '@/components/ui/fs-card';
import { getFileUrl } from '@/utils/file';

import { useAttachmentFile } from './useAttachmentFile';

export interface AttachmentCardProps {
  id: string;
  name: string;
  className?: string;
  /**
   * When false, the card is purely presentational: no media viewer opens on
   * click. Defaults to true.
   */
  interactive?: boolean;
}

/**
 * Read-only card for a stored attachment. The attachment only carries id and
 * name, so the file type is inferred from the extension and handed to the media
 * viewer (which loads/downloads the bytes itself) — no metadata request needed.
 */
export function AttachmentCard(props: AttachmentCardProps) {
  const { id, name, className, interactive = true } = props;
  const [viewerOpen, setViewerOpen] = useState<boolean>(false);

  const file = useAttachmentFile(id, name);
  const isImage = file.fileType.startsWith('image/');

  return (
    <>
      <FileCard
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        className={cn(interactive && 'cursor-pointer', className)}
        onClick={interactive ? () => setViewerOpen(true) : undefined}
        onKeyDown={
          interactive
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setViewerOpen(true);
                }
              }
            : undefined
        }
      >
        <FileCardContent>
          <FileIcon fileExtension={getFileExtension(name)} />
          <FileCardTitle className="min-w-0">{name}</FileCardTitle>
        </FileCardContent>
        {isImage && (
          <FileCardPreview>
            <FileCardPreviewImage className="opacity-85" src={getFileUrl(id)} />
            <FileCardPreviewFallback>
              <FeaturedIcon>
                <FileIcon />
              </FeaturedIcon>
            </FileCardPreviewFallback>
          </FileCardPreview>
        )}
      </FileCard>
      {interactive && <MediaViewer file={file} open={viewerOpen} onOpenChange={setViewerOpen} />}
    </>
  );
}
